import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { FileStack, FileText, CheckCircle2, Clock, ChevronDown, ChevronRight, AlertTriangle, Loader2 } from 'lucide-react';

// Document inventory for forensic accounting intake. Fetches from
// GET /api/v1/documents/list/{caseId} and groups files by inferred
// categories for court-exhibit review.

interface DocFile {
  id: string;
  filename: string;
  original_filename: string;
  document_type: string;
  doc_type: string;
  status: string;
  text_chars: number;
  extraction_method: string;
  created_at: string;
}

interface DocListResponse {
  claim_id: string;
  file_count: number;
  files: DocFile[];
}

interface CaseDocumentInventoryProps {
  caseId: string;
}

const GROUP_DEFINITIONS = [
  { name: "Financial records", keywords: ["bank", "merchant", "credit_card", "statement"] },
  { name: "Tax returns", keywords: ["1065", "k1", "tax", "k-1"] },
  { name: "Payroll", keywords: ["payroll"] },
  { name: "Policy & coverage", keywords: ["insurance_policy", "policy"] },
  { name: "Claim & legal", keywords: ["fnol", "proof_of_loss", "ror", "euo", "correspondence", "demand"] },
  { name: "Loss evidence", keywords: ["fire", "investigation", "service_record", "contractor", "invoice", "repair"] },
] as const;

function getGroupForFile(docType: string): string {
  const normalized = docType.toLowerCase();
  for (const group of GROUP_DEFINITIONS) {
    if (group.keywords.some(k => normalized.includes(k))) {
      return group.name;
    }
  }
  return "Other";
}

export default function CaseDocumentInventory({ caseId }: CaseDocumentInventoryProps) {
  const [data, setData] = useState<DocListResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fetchInventory = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<DocListResponse>(`/api/v1/documents/list/${caseId}`);
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Failed to load document inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (caseId) {
      fetchInventory();
    }
  }, [caseId]);

  const toggleGroup = (groupName: string) => {
    const next = new Set(expanded);
    if (next.has(groupName)) {
      next.delete(groupName);
    } else {
      next.add(groupName);
    }
    setExpanded(next);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 text-[var(--color-text-muted)] font-mono text-xs">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading document inventory…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border border-[var(--color-border)] rounded-[2px] bg-[var(--color-bg-surface)]">
        <div className="flex items-center gap-2 text-[var(--color-needs-human)] mb-2">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-xs uppercase tracking-wider font-mono">Error</span>
        </div>
        <p className="text-sm text-[var(--color-text-secondary)] mb-3">{error}</p>
        <button
          onClick={fetchInventory}
          className="px-3 py-1 text-xs uppercase tracking-wider border border-[var(--color-border)] rounded-[2px] hover:bg-[var(--color-bg-hover)] text-[var(--color-text-primary)]"
        >
          Retry
        </button>
      </div>
    );
  }

  // `!data.file_count` (not `=== 0`) so a degraded/partial payload with file_count
  // undefined also hits the empty state; and default `files` so a payload with a
  // count but no `files` array can't crash `.filter` (ErrorBoundary "Something went wrong").
  if (!data || !data.file_count || !Array.isArray(data.files)) {
    return (
      <div className="p-4 border border-[var(--color-border)] rounded-[2px] bg-[var(--color-bg-surface)] text-[var(--color-text-muted)] text-sm">
        No documents uploaded for this case.
      </div>
    );
  }

  const parsedCount = data.files.filter(f => f.status === 'parsed' || f.status === 'processed').length;
  const pendingCount = data.file_count - parsedCount;

  const groups: Record<string, DocFile[]> = {};
  for (const file of data.files) {
    const g = getGroupForFile(file.document_type);
    if (!groups[g]) groups[g] = [];
    groups[g].push(file);
  }

  const orderedGroupNames = [
    ...GROUP_DEFINITIONS.map(g => g.name),
    "Other"
  ].filter(name => groups[name] && groups[name].length > 0);

  return (
    <div className="border border-[var(--color-border)] rounded-[2px] bg-[var(--color-bg-surface)]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border-sub)]">
        <div className="flex items-center gap-2">
          <FileStack className="w-4 h-4 text-[var(--color-text-muted)]" />
          <span className="text-xs uppercase tracking-wider text-[var(--color-text-muted)] font-mono">
            Document Inventory
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono tabular-nums text-[var(--color-text-secondary)]">
          <span>{data.file_count} documents</span>
          <span className="text-[var(--color-border)]">·</span>
          <span className="text-[var(--color-verified)]">{parsedCount} parsed</span>
          {pendingCount > 0 && (
            <>
              <span className="text-[var(--color-border)]">·</span>
              <span className="text-[var(--color-needs-human)]">{pendingCount} pending</span>
            </>
          )}
        </div>
      </div>

      <div className="divide-y divide-[var(--color-border-sub)]">
        {orderedGroupNames.map(groupName => {
          const files = groups[groupName];
          const isExpanded = expanded.has(groupName);
          return (
            <div key={groupName} className="bg-[var(--color-bg-surface)]">
              <button
                onClick={() => toggleGroup(groupName)}
                aria-expanded={isExpanded}
                className="w-full flex items-center justify-between px-4 py-2 hover:bg-[var(--color-bg-hover)] transition-colors"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="w-3 h-3 text-[var(--color-text-muted)]" />
                  ) : (
                    <ChevronRight className="w-3 h-3 text-[var(--color-text-muted)]" />
                  )}
                  <span className="text-sm text-[var(--color-text-primary)]">{groupName}</span>
                </div>
                <span className="text-xs font-mono tabular-nums text-[var(--color-text-muted)] bg-[var(--color-bg-elevated)] px-1.5 py-0.5 rounded-[2px] border border-[var(--color-border-sub)]">
                  {files.length}
                </span>
              </button>

              {isExpanded && (
                <div className="max-h-80 overflow-y-auto border-t border-[var(--color-border-sub)] bg-[var(--color-bg-elevated)]">
                  <ul className="divide-y divide-[var(--color-border-sub)]">
                    {files.map(file => {
                      const isExtracted = file.status === 'parsed' || file.status === 'processed';
                      return (
                        <li key={file.id} className="px-4 py-2 flex items-center justify-between gap-4">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <FileText className="w-3 h-3 flex-shrink-0 text-[var(--color-text-dim)]" />
                            <span className="text-xs text-[var(--color-text-secondary)] truncate" title={file.original_filename}>
                              {file.original_filename}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            {file.text_chars > 0 && (
                              <span className="text-xs font-mono tabular-nums text-[var(--color-text-dim)]">
                                {file.text_chars.toLocaleString()}
                              </span>
                            )}
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[2px] text-[10px] uppercase tracking-wider font-mono border ${isExtracted ? 'border-[var(--color-verified)] text-[var(--color-verified)] bg-[var(--color-verified)]/10' : 'border-[var(--color-needs-human)] text-[var(--color-needs-human)] bg-[var(--color-needs-human)]/10'}`}>
                              {isExtracted ? (
                                <>
                                  <CheckCircle2 className="w-3 h-3" />
                                  <span>Parsed</span>
                                </>
                              ) : (
                                <>
                                  <Clock className="w-3 h-3" />
                                  <span>Pending</span>
                                </>
                              )}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}